import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/i18n";
import LanguageSelector from "@/components/LanguageSelector";
import {
  LayoutDashboard, Users, UserCircle, Network, Sparkles, GraduationCap,
  TerminalSquare, FolderLock, Banknote, Wallet, TrendingUp, ShieldCheck, UserCog, Target,
  Cloud, LogOut, ChevronDown, Palette, CreditCard, Clock, BookOpen, Monitor,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { BrandMark, BrandName } from "@/components/logo/BrandLogo";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import TrustedDevicesDialog from "@/components/TrustedDevicesDialog";
import api from "@/lib/api";
import { PaletteMenuSection } from "@/components/PaletteSwitcher";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { section: null, items: [{ key: "nav.dashboard", testId: "nav-dashboard", path: "/", icon: LayoutDashboard }] },
  {
    section: "nav.sectionWorkforce",
    items: [
      { key: "nav.employees", testId: "nav-employees", path: "/employees", icon: Users, roles: ["admin", "hr_manager", "dept_head"] },
      { key: "nav.myProfile", testId: "nav-my-profile", path: "/me", icon: UserCircle, roles: ["employee"] },
      { key: "nav.orgIntelligence", testId: "nav-org-intelligence", path: "/org", icon: Network },
      { key: "nav.rolesKpis", testId: "nav-roles-kpis", path: "/roles", icon: Target, roles: ["admin", "hr_manager", "dept_head"] },
    ],
  },
  {
    section: "nav.sectionIntelligence",
    items: [
      { key: "nav.skillsIntelligence", testId: "nav-skills-intelligence", path: "/skills", icon: Sparkles, roles: ["admin", "hr_manager", "dept_head"] },
      { key: "nav.learning", testId: "nav-learning-coursera-", path: "/learning", icon: GraduationCap },
      { key: "nav.openQuery", testId: "nav-open-query", path: "/query", icon: TerminalSquare, roles: ["admin", "hr_manager", "dept_head"] },
    ],
  },
  {
    section: "nav.sectionCompliance",
    items: [
      { key: "nav.documents", testId: "nav-documents-compliance", path: "/documents", icon: FolderLock },
      { key: "nav.payroll", testId: "nav-payroll", path: "/payroll", icon: Banknote, roles: ["admin", "hr_manager"] },
      { key: "nav.myPayslips", testId: "nav-my-payslips", path: "/payslips", icon: Wallet, roles: ["dept_head", "employee"] },
      { key: "nav.performance", testId: "nav-performance", path: "/performance", icon: TrendingUp },
    ],
  },
  {
    section: "nav.sectionSystem",
    items: [
      { key: "nav.securityConsole", testId: "nav-security-console", path: "/security", icon: ShieldCheck, roles: ["admin", "hr_manager"] },
      { key: "nav.userManagement", testId: "nav-user-management", path: "/admin/users", icon: UserCog, roles: ["admin"] },
      { key: "nav.branding", testId: "nav-branding", path: "/admin/branding", icon: Palette, roles: ["admin"] },
      { key: "nav.billing", testId: "nav-billing", path: "/billing", icon: CreditCard, roles: ["admin"] },
      { key: "nav.cloudConnections", testId: "nav-data-cloud-connections", path: "/cloud", icon: Cloud, roles: ["admin"] },
      { key: "nav.adminManual", testId: "nav-admin-manual", path: "/manual", icon: BookOpen, roles: ["admin", "hr_manager"] },
    ],
  },
];

const TITLES = [
  ["/employees/", "titles.employeeProfile"],
  ["/employees", "nav.employees"],
  ["/documents", "nav.documents"],
  ["/skills", "nav.skillsIntelligence"],
  ["/org", "nav.orgIntelligence"],
  ["/learning", "titles.learningCoursera"],
  ["/payroll", "nav.payroll"],
  ["/payslips", "nav.myPayslips"],
  ["/performance", "titles.performanceReviews"],
  ["/query", "nav.openQuery"],
  ["/cloud", "nav.cloudConnections"],
  ["/roles", "titles.rolesKpiMatrices"],
  ["/notifications", "titles.notifications"],
  ["/admin/users", "nav.userManagement"],
  ["/admin/branding", "nav.branding"],
  ["/billing", "nav.billing"],
  ["/security", "nav.securityConsole"],
  ["/", "nav.dashboard"],
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [devicesOpen, setDevicesOpen] = useState(false);
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.lang && user.lang !== i18n.language) setLanguage(user.lang).catch(() => {});
  }, [user?.lang]); // eslint-disable-line

  const titleKey = TITLES.find(([p]) => location.pathname.startsWith(p) && p !== "/")?.[1];
  const title = titleKey ? t(titleKey)
    : (location.pathname === "/" ? t("nav.dashboard") : branding?.product_name || t("titles.workspace"));

  const visible = (item) => !item.roles || item.roles.includes(user.role);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="app-sidebar w-64 shrink-0 flex flex-col fixed inset-y-0 left-0 z-30 overflow-y-auto"
        style={{ background: "var(--sidebar-bg)", color: "var(--sidebar-fg)" }}>
        <div className="px-5 py-6 flex items-center gap-3"
          style={{ borderBottom: "1px solid var(--sidebar-border)" }} data-testid="sidebar-logo">
          <BrandMark size={34} inverted />
          <div className="min-w-0">
            <div className="font-bold tracking-tight text-[16px] leading-none truncate">
              <BrandName />
            </div>
            <p className="text-[9px] uppercase tracking-[0.2em] mt-1.5" style={{ color: "var(--sidebar-muted)" }}>
              {t("nav.tagline")}
            </p>
          </div>
        </div>
        <nav className="flex-1 py-4" data-testid="sidebar-nav">
          {NAV.map(({ section, items }) => {
            const shown = items.filter(visible);
            if (!shown.length) return null;
            return (
              <div key={section || "root"} className="mb-1">
                {section && (
                  <p className="px-5 pt-4 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]"
                    style={{ color: "var(--sidebar-muted)" }}>
                    {t(section)}
                  </p>
                )}
                {shown.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink key={item.key} to={item.path} end={item.path === "/"} data-testid={item.testId}
                      className="flex items-center gap-3 px-5 py-2.5 text-[13px] transition-colors"
                      style={({ isActive }) => ({
                        borderInlineStart: `2px solid ${isActive ? "var(--brand-accent)" : "transparent"}`,
                        color: isActive ? "var(--sidebar-fg)" : "var(--sidebar-muted)",
                        background: isActive ? "var(--sidebar-hover)" : "transparent",
                        fontWeight: isActive ? 500 : 400,
                      })}>
                      <Icon className="h-4 w-4" strokeWidth={2} />
                      <span>{t(item.key)}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="px-5 py-4 text-[11px] font-mono"
          style={{ borderTop: "1px solid var(--sidebar-border)", color: "var(--sidebar-muted)" }}
          data-testid="sidebar-tenant-name">
          {user?.tenant?.org_name || branding?.product_name || ""}
        </div>
      </aside>

      <div className="app-content flex-1 ml-64 flex flex-col min-w-0">
        {branding?.status === "trial" && branding?.trial && (
          <div className="bg-amber-50 border-b border-amber-200 px-8 py-2 flex items-center justify-between"
            data-testid="trial-banner">
            <p className="text-[12px] text-amber-800 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" />
              <b>{t("shell.trial")}</b> — {t("shell.daysLeft", { count: branding.trial.days_left })}
            </p>
            <button data-testid="trial-upgrade-cta" onClick={() => navigate("/billing")}
              className="text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1 brand-accent-bg">
              {t("common.upgrade")}
            </button>
          </div>
        )}
        {branding?.status === "past_due" && (
          <div className="bg-red-50 border-b border-red-200 px-8 py-2 flex items-center justify-between"
            data-testid="pastdue-banner">
            <p className="text-[12px] text-red-800 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" />
              <b>{t("shell.paymentFailed")}</b> — {t("shell.serviceContinuesUntil")}{" "}
              {branding.past_due?.grace_ends_at
                ? new Date(branding.past_due.grace_ends_at).toLocaleDateString("en-GB",
                    { day: "2-digit", month: "short", year: "numeric" })
                : t("shell.gracePeriodEnds")}
            </p>
            <button data-testid="pastdue-resolve-cta" onClick={() => navigate("/billing")}
              className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white px-3 py-1 bg-red-600 hover:bg-red-700">
              {t("shell.resolvePayment")}
            </button>
          </div>
        )}
        <header className="bg-white border-b border-border px-8 h-16 flex items-center justify-between sticky top-0 z-20 gap-6">
          <h1 className="text-lg font-bold tracking-tight text-[var(--brand-primary)] whitespace-nowrap" data-testid="topbar-title">
            {title}
          </h1>
          <GlobalSearch />
          <div className="flex items-center gap-2">
            <LanguageSelector persistToProfile />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="topbar-user-menu"
                  className="flex items-center gap-2.5 pl-3 pr-2 py-1.5 hover:bg-slate-50 border border-transparent hover:border-border transition-colors">
                  <div className="h-8 w-8 bg-[var(--brand-primary)] text-white flex items-center justify-center text-[12px] font-semibold">
                    {user.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                  <div className="text-left">
                    <p className="text-[13px] font-medium text-slate-800 leading-none">{user.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-teal-700 mt-1" data-testid="user-role-badge">
                      {t(`roles.${user.role}`)}
                    </p>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56">
                <DropdownMenuLabel className="text-[12px]">{user.email}</DropdownMenuLabel>
                {user.role === "admin" && (
                  <>
                    <DropdownMenuSeparator />
                    <PaletteMenuSection />
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="trusted-devices-menu-item"
                  className="rounded-none text-[13px] cursor-pointer"
                  onSelect={(e) => { e.preventDefault(); setDevicesOpen(true); }}>
                  <Monitor className="h-4 w-4" /> {t("shell.trustedDevices")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  className="rounded-none text-[13px] cursor-pointer"
                  onClick={() => { logout(); navigate("/login"); }}>
                  <LogOut className="h-4 w-4" /> {t("common.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <TrustedDevicesDialog open={devicesOpen} onOpenChange={setDevicesOpen}
              client={api} basePath="/auth" title={t("shell.trustedDevices")} />
          </div>
        </header>
        <main className="flex-1 px-8 py-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
